(() => {
  'use strict';

  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  function initDrawers() {
    const menu = qs('[data-egho-menu-drawer]');
    const search = qs('[data-egho-search-drawer]');
    const overlay = qs('[data-egho-overlay]');
    const menuToggle = qs('[data-egho-menu-toggle]');
    const searchToggle = qs('[data-egho-search-toggle]');
    let activeDrawer = null;
    let opener = null;

    if (!menu || !search || !overlay) return;

    function closeDrawers({ restoreFocus = true } = {}) {
      [menu, search].forEach((drawer) => {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
      });
      [menuToggle, searchToggle].forEach((button) => button?.setAttribute('aria-expanded', 'false'));
      overlay.classList.remove('is-open');
      document.body.classList.remove('drawer-open');
      window.setTimeout(() => { overlay.hidden = true; }, 230);
      if (restoreFocus) opener?.focus();
      activeDrawer = null;
    }

    function openDrawer(drawer, toggle) {
      if (!drawer) return;
      closeDrawers({ restoreFocus: false });
      activeDrawer = drawer;
      opener = toggle;
      overlay.hidden = false;
      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        overlay.classList.add('is-open');
      });
      drawer.setAttribute('aria-hidden', 'false');
      toggle?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('drawer-open');
      const focusTarget = qs('[data-egho-search-input]', drawer) || qs('a, button, input', drawer);
      window.setTimeout(() => focusTarget?.focus(), 250);
    }

    menuToggle?.addEventListener('click', () => openDrawer(menu, menuToggle));
    searchToggle?.addEventListener('click', () => openDrawer(search, searchToggle));
    qs('[data-egho-menu-close]')?.addEventListener('click', () => closeDrawers());
    qs('[data-egho-search-close]')?.addEventListener('click', () => closeDrawers());
    overlay.addEventListener('click', () => closeDrawers());
    qsa('[data-egho-drawer-link]').forEach((link) => link.addEventListener('click', () => closeDrawers({ restoreFocus: false })));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && activeDrawer) closeDrawers();
      if (event.key !== 'Tab' || !activeDrawer) return;
      const focusable = qsa('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', activeDrawer)
        .filter((node) => !node.hidden && node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function initPredictiveSearch() {
    const input = qs('[data-egho-search-input]');
    const results = qs('[data-egho-predictive-search]');
    if (!input || !results) return;
    let timer = 0;
    let controller = null;

    function clearResults() {
      results.replaceChildren();
      results.hidden = true;
    }

    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const term = input.value.trim();
      if (term.length < 2) {
        controller?.abort();
        clearResults();
        return;
      }
      timer = window.setTimeout(async () => {
        try {
          controller?.abort();
          controller = new AbortController();
          results.hidden = false;
          results.innerHTML = '<p class="predictive-search__empty">Aranıyor…</p>';
          const root = window.Shopify?.routes?.root || '/';
          const url = `${root}search/suggest?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
          const response = await fetch(url, { signal: controller.signal, headers: { Accept: 'text/html' } });
          if (!response.ok) throw new Error('Predictive search failed');
          results.innerHTML = await response.text();
          results.hidden = false;
        } catch (error) {
          if (error.name !== 'AbortError') results.innerHTML = '<p class="predictive-search__empty">Arama şu anda kullanılamıyor.</p>';
        }
      }, 220);
    });
  }

  function initProductCards() {
    qsa('[data-product-card]').forEach((card) => {
      const image = qs('.egho-product-card__image, .product-card__image', card);
      const links = qsa('[data-card-product-link]', card);
      const colorLabel = qs('[data-card-color-label]', card);
      const price = qs('[data-card-price]', card);
      const compare = qs('[data-card-compare-price]', card);

      qsa('[data-card-swatch]', card).forEach((swatch) => {
        swatch.addEventListener('click', (event) => {
          event.preventDefault();
          const nextImage = swatch.dataset.imageUrl;
          const nextUrl = swatch.dataset.productUrl;
          if (image && nextImage) {
            image.src = nextImage;
            image.srcset = '';
            image.alt = swatch.dataset.imageAlt || image.alt;
          }
          if (nextUrl) links.forEach((link) => { link.href = nextUrl; });
          if (colorLabel) colorLabel.textContent = swatch.title || swatch.dataset.color || '';
          if (price && swatch.dataset.price) price.textContent = swatch.dataset.price;
          if (compare) {
            compare.textContent = swatch.dataset.comparePrice || '';
            compare.hidden = !swatch.dataset.comparePrice;
          }
          qsa('[data-card-swatch]', card).forEach((item) => {
            const selected = item === swatch;
            item.classList.toggle('is-active', selected);
            if (selected) item.setAttribute('aria-current', 'true');
            else item.removeAttribute('aria-current');
          });
        });
      });
    });
  }

  function initProductRails() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    qsa('[data-product-rail]').forEach((viewport) => {
      const track = qs('[data-product-rail-track]', viewport);
      if (!track) return;
      const visibleCount = window.matchMedia('(max-width: 720px)').matches ? 2 : 4;
      if (track.children.length <= visibleCount) return;

      let paused = false;
      let animating = false;
      viewport.classList.add('is-rotating');

      function rotate() {
        if (paused || animating || document.hidden) return;
        const firstCard = track.firstElementChild;
        if (!firstCard) return;
        const gap = parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap || '0');
        const distance = firstCard.getBoundingClientRect().width + gap;
        animating = true;
        track.style.transition = 'transform 480ms ease';
        track.style.transform = `translate3d(-${distance}px,0,0)`;

        const finish = () => {
          track.style.transition = 'none';
          track.style.transform = 'translate3d(0,0,0)';
          track.appendChild(firstCard);
          track.getBoundingClientRect();
          animating = false;
        };
        track.addEventListener('transitionend', finish, { once: true });
        window.setTimeout(() => { if (animating) finish(); }, 620);
      }

      const timer = window.setInterval(rotate, 3600);
      viewport.addEventListener('mouseenter', () => { paused = true; });
      viewport.addEventListener('mouseleave', () => { paused = false; });
      viewport.addEventListener('focusin', () => { paused = true; });
      viewport.addEventListener('focusout', () => { paused = false; });
      window.addEventListener('pagehide', () => window.clearInterval(timer), { once: true });
    });
  }

  function initCollectionTools() {
    qsa('[data-collection-color-filter]').forEach((select) => {
      const section = select.closest('.egho-collection') || document;
      const cards = qsa('[data-product-card]', section);
      const empty = qs('[data-filter-empty]', section);
      const apply = () => {
        const value = select.value;
        let visible = 0;
        cards.forEach((card) => {
          const show = !value || card.dataset.color === value;
          card.hidden = !show;
          if (show) visible += 1;
        });
        if (empty) empty.hidden = visible !== 0;
      };
      select.addEventListener('change', apply);
      apply();
    });

    qsa('[data-collection-sort]').forEach((select) => {
      select.addEventListener('change', () => select.form?.requestSubmit());
    });
  }

  const colorMap = {
    siyah: '#111111', black: '#111111', beyaz: '#f7f7f5', white: '#f7f7f5',
    ekru: '#eee7da', ecru: '#eee7da', bej: '#d5c1a3', beige: '#d5c1a3',
    kahverengi: '#684b3a', kahve: '#684b3a', brown: '#684b3a', lacivert: '#172136', navy: '#172136',
    gri: '#969696', gray: '#969696', grey: '#969696', nefti: '#394b3c', petrol: '#245a60',
    haki: '#676b46', khaki: '#676b46', naturel: '#d4c2a1', natural: '#d4c2a1',
    mavi: '#638aa4', blue: '#638aa4', mint: '#9dbba8', vizon: '#8a7462',
    antrasit: '#44464c', anthracite: '#44464c', krem: '#e9deca', cream: '#e9deca',
    bordo: '#6b2334', burgundy: '#6b2334', kirmizi: '#b53232', red: '#b53232',
    pembe: '#d69ca9', pink: '#d69ca9', sari: '#d3ad31', yellow: '#d3ad31',
    turuncu: '#c66b2d', orange: '#c66b2d', mor: '#765b8d', purple: '#765b8d'
  };

  function colorKey(value) {
    return String(value || '')
      .toLocaleLowerCase('tr-TR')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ı/g, 'i')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function paintProductColorDots(scope = document) {
    qsa('[data-color-name]', scope).forEach((dot) => {
      const key = colorKey(dot.dataset.colorName);
      const matched = Object.keys(colorMap).find((name) => key.includes(name));
      dot.style.background = matched ? colorMap[matched] : '#b8b8b8';
    });
  }

  function formatMoney(cents) {
    const activeCurrency = window.Shopify?.currency?.active || 'TRY';
    const locale = document.documentElement.lang || 'tr-TR';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency: activeCurrency }).format(Number(cents || 0) / 100);
    } catch (_) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${activeCurrency}`;
    }
  }

  function initProducts() {
    qsa('[data-product-root]').forEach((root) => {
      const variantsNode = qs('[data-product-variants]', root);
      if (!variantsNode) return;
      let variants = [];
      try { variants = JSON.parse(variantsNode.textContent); } catch (_) { return; }

      const optionGroups = qsa('[data-product-option]', root);
      const colorOptionIndex = Number(root.dataset.colorOptionIndex ?? -1);
      const variantInput = qs('[data-variant-id-input]', root);
      const price = qs('[data-product-price]', root);
      const compare = qs('[data-product-compare-price]', root);
      const status = qs('[data-product-status]', root);
      const addButton = qs('[data-add-to-cart]', root);
      const addText = qs('[data-add-to-cart-text]', root);

      const selectedValues = () => optionGroups.map((group) => qs('[data-option-value].is-selected', group)?.dataset.optionValue || '');

      const mediaTrack = qs('[data-product-media-track]', root);
      const galleryCurrent = qs('[data-product-gallery-current]', root);
      const galleryTotal = qs('[data-product-gallery-total]', root);
      const galleryPrev = qs('[data-product-gallery-prev]', root);
      const galleryNext = qs('[data-product-gallery-next]', root);
      const noColorMedia = qs('[data-product-no-color-media]', root);
      const galleryCount = galleryCurrent?.closest('.egho-product__gallery-count');
      const mediaItems = qsa('[data-product-media-item]', root);
      const mediaButtons = qsa('[data-media-target]', root);
      let visibleMediaItems = mediaItems.slice();


      // EGHO gallery: only explicit color labels, verified theme overrides and
      // actual variant media relationships count as color-to-image evidence.
      // Gallery position and numeric filename similarity are NOT evidence.
      const normalizeGalleryColor = (value) =>
        String(value || '').trim().toLocaleLowerCase('tr-TR');
      const colorAssignments = new Map();
      const disputedMediaIds = new Set();
      const assignColor = (mediaId, color, confidence) => {
        const id = String(mediaId || '');
        const normalized = normalizeGalleryColor(color);
        if (!id || !normalized || disputedMediaIds.has(id)) return;
        const previous = colorAssignments.get(id);
        if (!previous || confidence > previous.confidence) {
          colorAssignments.set(id, { color: normalized, confidence });
        } else if (confidence === previous.confidence && previous.color !== normalized) {
          colorAssignments.delete(id);
          disputedMediaIds.add(id);
        }
      };
      const variantColor = (variant) => variant?.options?.[colorOptionIndex] || '';
      if (colorOptionIndex >= 0) {
        mediaItems.forEach((item) => {
          const mediaId = item.dataset.mediaId;
          const explicitColor = item.dataset.mediaColor;
          const alt = String(item.dataset.mediaAlt || '');
          const altMatch = alt.match(/(?:^|\|)\s*(?:renk|colou?r)\s*:\s*([^|]+)/i);
          if (altMatch) assignColor(mediaId, altMatch[1], 3);
          // Match only complete color words from this product's variant options.
          // Examples: Siyah-01.jpg, Lacivert_02.jpg, Bebe_Mavi-03.jpg.
          const filename = String(item.dataset.mediaFilename || '');
          let decodedFilename = filename;
          try { decodedFilename = decodeURIComponent(filename); } catch (_) {}
          const filenameParts = normalizeGalleryColor(decodedFilename)
            .replace(/\.[a-z0-9]+$/i, '')
            .split(/[-_.\s]+/)
            .filter(Boolean);
          const matchingFilenameColors = [...new Set(variants.map(variantColor))]
            .filter(Boolean)
            .filter((candidate) => {
              const words = normalizeGalleryColor(candidate).split(/\s+/).filter(Boolean);
              return words.length && filenameParts.some((part, i) =>
                words.every((word, j) => filenameParts[i + j] === word)
              );
            });
          if (matchingFilenameColors.length === 1) assignColor(mediaId, matchingFilenameColors[0], 3);
          if (explicitColor) assignColor(mediaId, explicitColor, 4);
        });
        // Shopify variants may share the same featured image across sizes.
        // Only their actual featured media IDs are considered.
        variants.forEach((variant) => {
          const featuredId = variant.featured_media?.id || variant.featured_image?.id;
          assignColor(featuredId, variantColor(variant), 2);
        });
      }
      function mediaForColor(color, preferredMediaId) {
        if (colorOptionIndex < 0 || !color) return mediaItems.slice();
        const wanted = normalizeGalleryColor(color);
        const confirmed = mediaItems.filter(
          (item) => colorAssignments.get(String(item.dataset.mediaId))?.color === wanted
        );
        // SAFE FALLBACK: a variant's hero photo does NOT prove the remaining
        // detail photographs belong to that or any other color.
        // Until every photo is positively mapped, show ALL product photos;
        // never collapse a multi-image gallery to a single hero image.
        // This is preferable to deleting/hiding legitimate detail shots.
        const containsUnmapped = mediaItems.some(
          (item) => !colorAssignments.has(String(item.dataset.mediaId))
        );
        // When photos themselves are not mapped, preserve the full gallery.
        if (containsUnmapped) {
          const hasExplicitColorMetadata = mediaItems.some((item) => {
            const entry = colorAssignments.get(String(item.dataset.mediaId));
            return entry && entry.confidence >= 3;
          });
          // A newly uploaded unlabeled photo must not break an already mapped gallery.
          // Unlabeled items stay hidden until an ALT/filename color matches.
          if (!hasExplicitColorMetadata) {
            const preferred = mediaItems.find(
              (item) => String(item.dataset.mediaId) === String(preferredMediaId)
            );
            return preferred
              ? [preferred, ...mediaItems.filter((item) => item !== preferred)]
              : mediaItems.slice();
          }
        }
        // All photos are mapped, but none belong to this color:
        // do NOT show another color's photos.
        if (!confirmed.length) return [];
        const featured = confirmed.find(
          (item) => String(item.dataset.mediaId) === String(preferredMediaId)
        );
        return featured ? [featured, ...confirmed.filter((item) => item !== featured)] : confirmed;
      }

      function selectedColor() {
        if (colorOptionIndex < 0) return '';
        return selectedValues()[colorOptionIndex] || root.dataset.initialColor || '';
      }


      function galleryIndex(item) {
        return Math.max(0, visibleMediaItems.indexOf(item));
      }

      function markActiveMedia(item) {
        if (!item) return;
        mediaItems.forEach((entry) => entry.classList.toggle('is-active', entry === item));
        if (galleryCurrent) galleryCurrent.textContent = String(galleryIndex(item) + 1);
        mediaButtons.forEach((button) => {
          const active = String(button.dataset.mediaTarget) === String(item.dataset.mediaId);
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-current', active ? 'true' : 'false');
        });
      }

      function activateMedia(mediaId, immediate = false) {
        const target = visibleMediaItems.find((item) => String(item.dataset.mediaId) === String(mediaId))
          || visibleMediaItems[0];
        if (!target) return;
        markActiveMedia(target);
        if (mediaTrack) {
          mediaTrack.scrollTo({
            left: mediaTrack.clientWidth * galleryIndex(target),
            behavior: immediate ? 'auto' : 'smooth'
          });
        }
      }

      function updateGalleryForColor(color, preferredMediaId) {
        visibleMediaItems = mediaForColor(color, preferredMediaId);
        const missingColorPhotos = visibleMediaItems.length === 0 && colorOptionIndex >= 0;
        if (noColorMedia) {
          noColorMedia.hidden = !missingColorPhotos;
          noColorMedia.closest('[data-product-carousel]')?.classList.toggle('has-no-color-media', missingColorPhotos);
        }
        if (galleryCount) galleryCount.hidden = !visibleMediaItems.length;
        const visibleIds = new Set(visibleMediaItems.map((item) => String(item.dataset.mediaId)));
        // CSS flex order ensures a newly selected variant's main image is
        // the first slide, even if its Shopify position is 29 or 30.
        const visibleOrder = new Map(visibleMediaItems.map((item, index) => [item, index]));
        mediaItems.forEach((item, index) => {
          item.hidden = !visibleIds.has(String(item.dataset.mediaId));
          item.style.order = String(visibleOrder.get(item) ?? mediaItems.length + index);
        });
        mediaButtons.forEach((button) => {
          button.hidden = !visibleIds.has(String(button.dataset.mediaTarget));
        });
        if (galleryTotal) galleryTotal.textContent = String(visibleMediaItems.length);
        const hasMultiple = visibleMediaItems.length > 1;
        if (galleryPrev) galleryPrev.hidden = !hasMultiple;
        if (galleryNext) galleryNext.hidden = !hasMultiple;
        if (mediaTrack) mediaTrack.scrollLeft = 0;
        activateMedia(visibleMediaItems[0]?.dataset.mediaId, true);
      }

      function updateOptionAvailability() {
        const selected = selectedValues();
        optionGroups.forEach((group, groupIndex) => {
          qsa('[data-option-value]', group).forEach((button) => {
            const optionValue = button.dataset.optionValue;
            const isColor = groupIndex === colorOptionIndex || group.dataset.isColor === 'true';
            const possible = variants.some((variant) => {
              if (!variant.available || variant.options?.[groupIndex] !== optionValue) return false;
              if (isColor) return true;
              return selected.every((value, index) => index === groupIndex || !value || variant.options?.[index] === value);
            });
            // Sold-out colors must remain viewable, while sold-out sizes and
            // add-to-cart still respect actual inventory.
            const selectable = isColor
              ? variants.some((variant) => variant.options?.[groupIndex] === optionValue)
              : possible;
            button.classList.toggle('is-unavailable', !possible);
            button.setAttribute('aria-disabled', selectable ? 'false' : 'true');
            button.disabled = !selectable;
          });
        });
      }

      function updateVariant() {
        const selected = selectedValues();
        const selectionComplete = selected.length > 0 && selected.every(Boolean);
        const exactVariant = selectionComplete
          ? variants.find((item) => item.options.every((value, index) => value === selected[index]))
          : null;
        const displayVariant = exactVariant
          || variants.find((item) => item.available && (colorOptionIndex < 0 || item.options?.[colorOptionIndex] === selectedColor()))
          || variants.find((item) => colorOptionIndex >= 0 && item.options?.[colorOptionIndex] === selectedColor())
          || variants.find((item) => item.available)
          || variants[0];
        if (!displayVariant) return;

        if (variantInput) variantInput.value = exactVariant?.id || '';
        if (price) price.textContent = formatMoney(displayVariant.price);
        if (compare) {
          const onSale = Number(displayVariant.compare_at_price || 0) > Number(displayVariant.price || 0);
          compare.textContent = onSale ? formatMoney(displayVariant.compare_at_price) : '';
          compare.classList.toggle('is-hidden', !onSale);
        }
        const canAdd = Boolean(exactVariant?.available);
        if (status) {
          status.textContent = canAdd ? 'Stokta' : (selectionComplete ? 'Tükendi' : 'Beden seçiniz');
          status.classList.toggle('is-unavailable', selectionComplete && !canAdd);
        }
        if (addButton) addButton.disabled = !canAdd;
        if (addText) addText.textContent = canAdd ? 'Sepete Ekle' : (selectionComplete ? 'Tükendi' : 'Beden Seç');

        const mediaId = displayVariant.featured_media?.id || displayVariant.featured_image?.id;
        updateGalleryForColor(selectedColor(), mediaId);
        updateOptionAvailability();
        // An incomplete / sold-out color-size combination must not leave
        // a stale variant from the previously selected color in the URL.
        const url = new URL(window.location.href);
        if (exactVariant) {
          url.searchParams.set('variant', exactVariant.id);
        } else {
          url.searchParams.delete('variant');
        }
        window.history.replaceState({}, '', url);
      }

      optionGroups.forEach((group, groupIndex) => {
        qsa('[data-option-value]', group).forEach((button) => {
          button.addEventListener('click', () => {
            if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;
            qsa('[data-option-value]', group).forEach((item) => {
              const selected = item === button;
              item.classList.toggle('is-selected', selected);
              item.setAttribute('aria-pressed', selected ? 'true' : 'false');
            });
            const label = qs('[data-selected-option-value]', group);
            if (label) label.textContent = button.dataset.optionValue || '';
            if (groupIndex === colorOptionIndex || group.dataset.isColor === 'true') {
              optionGroups.forEach((otherGroup, otherIndex) => {
                if (otherIndex === groupIndex) return;
                const selectedButton = qs('[data-option-value].is-selected', otherGroup);
                if (!selectedButton) return;
                const color = button.dataset.optionValue;
                const stillAvailable = variants.some((variant) => variant.available && variant.options?.[colorOptionIndex] === color && variant.options?.[otherIndex] === selectedButton.dataset.optionValue);
                if (!stillAvailable) {
                  selectedButton.classList.remove('is-selected');
                  selectedButton.setAttribute('aria-pressed', 'false');
                  const otherLabel = qs('[data-selected-option-value]', otherGroup);
                  if (otherLabel) otherLabel.textContent = 'Seçiniz';
                }
              });
            }
            updateVariant();
          });
        });
      });

      mediaButtons.forEach((button) => {
        button.addEventListener('click', () => activateMedia(button.dataset.mediaTarget));
      });

      function moveGallery(direction) {
        if (!mediaTrack || !visibleMediaItems.length) return;
        const current = visibleMediaItems.find((item) => item.classList.contains('is-active')) || visibleMediaItems[0];
        const nextIndex = (galleryIndex(current) + direction + visibleMediaItems.length) % visibleMediaItems.length;
        activateMedia(visibleMediaItems[nextIndex]?.dataset.mediaId);
      }
      galleryPrev?.addEventListener('click', () => moveGallery(-1));
      galleryNext?.addEventListener('click', () => moveGallery(1));
      mediaTrack?.addEventListener('scroll', () => {
        if (!galleryCurrent) return;
        const width = Math.max(1, mediaTrack.getBoundingClientRect().width);
        const index = Math.min(visibleMediaItems.length - 1, Math.round(mediaTrack.scrollLeft / width));
        markActiveMedia(visibleMediaItems[Math.max(0, index)]);
      }, { passive: true });

      if (mediaItems.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        window.setTimeout(() => {
          if (document.hidden || !mediaTrack || mediaTrack.matches(':hover')) return;
          moveGallery(1);
        }, 1800);
      }

      paintProductColorDots(root);
      updateVariant();
    });
  }

  function initCart() {
    const cartItems = qsa('[data-cart-item]');

    function showCartImage(container, url) {
      const image = qs('[data-cart-image]', container);
      if (!image || !url) return false;
      const normalizedUrl = String(url).startsWith('//') ? `https:${url}` : String(url);
      image.srcset = '';
      image.src = normalizedUrl;
      image.classList.remove('is-loading');
      qs('.egho-cart-item__image-status', container)?.setAttribute('hidden', '');
      return true;
    }

    cartItems.forEach((container) => {
      showCartImage(container, container.dataset.cartVariantImage || container.dataset.cartProductImage);
    });

    qsa('[data-cart-image]').forEach((image) => {
      if (image.complete && image.naturalWidth > 1) {
        image.classList.remove('is-loading');
        image.parentElement?.querySelector('.egho-cart-item__image-status')?.setAttribute('hidden', '');
      }
      image.addEventListener('load', () => {
        if (image.naturalWidth > 1) {
          image.classList.remove('is-loading');
          image.parentElement?.querySelector('.egho-cart-item__image-status')?.setAttribute('hidden', '');
        }
      });
      image.addEventListener('error', () => {
        const fallback = image.dataset.cartImageFallback;
        if (fallback && image.src !== fallback) {
          image.srcset = '';
          image.src = fallback;
        }
      });
    });

    if (cartItems.length) {
      fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('Cart unavailable')))
        .then((cart) => Promise.all(cartItems.map(async (container) => {
          const line = (cart.items || []).find((item) => item.key === container.dataset.lineKey || String(item.variant_id) === container.dataset.cartVariantId);
          const ajaxImage = typeof line?.image === 'string' ? line.image : line?.featured_image?.url;
          if (showCartImage(container, ajaxImage)) return;
          const handle = container.dataset.cartProductHandle;
          if (!handle) return;
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`);
          if (!response.ok) return;
          const product = await response.json();
          const variant = (product.variants || []).find((item) => String(item.id) === container.dataset.cartVariantId);
          showCartImage(container, variant?.featured_image?.src || product.featured_image || product.images?.[0]);
        })))
        .catch(() => {});
    }

    qsa('.egho-cart-item__quantity-control').forEach((control) => {
      const input = qs('[data-cart-quantity]', control);
      const minus = qs('[data-cart-quantity-minus]', control);
      const plus = qs('[data-cart-quantity-plus]', control);
      if (!input) return;
      const line = control.dataset.cartLine;
      const changeUrl = control.dataset.cartChangeUrl;

      function changeQuantity(delta) {
        const current = Number.parseInt(input.value, 10) || 0;
        const next = Math.max(0, current + delta);
        if (next === current) return;
        input.value = String(next);
        if (changeUrl && line) {
          const url = new URL(changeUrl, window.location.origin);
          url.searchParams.set('line', line);
          url.searchParams.set('quantity', String(next));
          window.location.assign(url.toString());
        }
      }

      minus?.addEventListener('click', () => changeQuantity(-1));
      plus?.addEventListener('click', () => changeQuantity(1));
      input.addEventListener('change', () => {
        if (Number(input.value) < 0) input.value = '0';
      });
    });
  }

  function initInfoModals() {
    const modal = qs('[data-egho-info-modal]');
    const dialog = qs('.egho-info-modal__dialog', modal);
    if (!modal || !dialog) return;
    let opener = null;
    function close() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('info-modal-open');
      window.setTimeout(() => { modal.hidden = true; }, 260);
      opener?.focus();
    }
    qsa('[data-egho-info-open]').forEach((button) => button.addEventListener('click', () => {
      opener = button;
      qsa('[data-egho-info-content]', modal).forEach((content) => { content.hidden = content.dataset.eghoInfoContent !== button.dataset.eghoInfoOpen; });
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('info-modal-open');
      requestAnimationFrame(() => modal.classList.add('is-open'));
      window.setTimeout(() => dialog.focus(), 260);
    }));
    qsa('[data-egho-info-close]', modal).forEach((button) => button.addEventListener('click', close));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && modal.classList.contains('is-open')) close(); });
  }

  function initCookieConsent() {
    const banner = qs('[data-egho-cookie]');
    if (!banner || window.localStorage.getItem('egho-cookie-choice')) return;
    window.setTimeout(() => { banner.hidden = false; requestAnimationFrame(() => banner.classList.add('is-visible')); }, 700);
    function choose(accepted) {
      const consent = { analytics: accepted, marketing: accepted, preferences: accepted, sale_of_data: accepted };
      if (window.Shopify?.customerPrivacy?.setTrackingConsent) window.Shopify.customerPrivacy.setTrackingConsent(consent, () => {});
      window.localStorage.setItem('egho-cookie-choice', accepted ? 'all' : 'necessary');
      banner.classList.remove('is-visible');
      window.setTimeout(() => { banner.hidden = true; }, 250);
    }
    qs('[data-cookie-accept]', banner)?.addEventListener('click', () => choose(true));
    qs('[data-cookie-reject]', banner)?.addEventListener('click', () => choose(false));
  }

  function init() {
    initDrawers();
    initPredictiveSearch();
    initProductCards();
    initProductRails();
    initCollectionTools();
    initProducts();
    initCart();
    initInfoModals();
    initCookieConsent();
    paintProductColorDots();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
